import { useState } from "react"
import { useDispatch } from "react-redux"
import { StoreDispatch } from "../../store/configure-store"
import { FormInputText } from "../../cmps/forms/input/form-input-text"
import { RequiredAsterisk } from "../../cmps/forms/required-asterisk"
import { Backlog } from "../../cmps/util-cmps/backlog"
import { Spinner } from "../../cmps/util-cmps/spinner"
import { UserMsg } from "../../models/user"
import { setUserMsg } from "../../store/user"
import { httpService } from "../../services/http-service"
import { createSystemMutation } from "../../cmps/util-cmps/graphql-mutations/create-system"
import { updateSystemMutation } from "../../cmps/util-cmps/graphql-mutations/update-system"
import { useEscapeKey } from "../../hooks/use-escape-key"
import { useScrollLock } from "../../hooks/use-scroll-lock"
import { useFocusTrap } from "../../hooks/use-focus-trap"

interface SystemFormModalProps {
    systemToEdit?: any
    existingTypes?: string[]
    existingNames?: string[]
    onSuccess: () => void
    onClose: () => void
}

export const SystemFormModal: React.FC<SystemFormModalProps> = ({
    systemToEdit, existingTypes = [], existingNames = [], onSuccess, onClose
}) => {
    const isEditMode = !!systemToEdit
    const dispatch = useDispatch<StoreDispatch>()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const [form, setForm] = useState(() => isEditMode ? {
        name: systemToEdit.name || '',
        abbreviation: systemToEdit.abbreviation || '',
        type: systemToEdit.type || '',
        logoUrl: systemToEdit.logoUrl || '',
    } : {
        name: '',
        abbreviation: '',
        type: '',
        logoUrl: '',
    })

    useEscapeKey(onClose)
    useScrollLock()
    const trapRef = useFocusTrap<HTMLDivElement>()

    const onChangeText = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target
        setForm(prev => ({ ...prev, [name]: value }))
    }

    const validate = (): string | null => {
        if (!form.name.trim()) return 'Name is required'
        if (!form.abbreviation.trim()) return 'Abbreviation is required'
        if (!form.type) return 'Type is required'
        const nameToCheck = form.name.trim().toLowerCase()
        const originalName = systemToEdit?.name?.toLowerCase()
        if (existingNames.some(n => n.toLowerCase() === nameToCheck && n.toLowerCase() !== originalName)) {
            return 'A system with this name already exists'
        }
        return null
    }

    const onSubmit = async () => {
        const error = validate()
        if (error) {
            const msg: UserMsg = { type: 'danger', txt: error }
            dispatch(setUserMsg({ msg }))
            return
        }

        setIsSubmitting(true)
        try {
            if (isEditMode) {
                const query = updateSystemMutation()
                const variables = {
                    systemId: Number(systemToEdit.id),
                    name: form.name.trim(),
                    abbreviation: form.abbreviation.trim(),
                    type: form.type,
                    logoUrl: form.logoUrl.trim() || null,
                }
                console.log('About to call updateSystem with:', { query, variables })
                const res = await httpService.graphql('auditech', { query, variables })
                console.log('updateSystem response:', JSON.stringify(res))
                if (!res?.updateSystem?.system) throw new Error('Update failed')
                const msg: UserMsg = { type: 'success', txt: 'System updated successfully' }
                dispatch(setUserMsg({ msg }))
            } else {
                const query = createSystemMutation(
                    form.name.trim(),
                    form.abbreviation.trim(),
                    form.type,
                    form.logoUrl.trim() || null
                )
                const res = await httpService.graphql('auditech', query)
                console.log('createSystem response:', JSON.stringify(res))
                if (!res?.createSystem?.system) throw new Error('Create failed')
                const msg: UserMsg = { type: 'success', txt: 'System created successfully' }
                dispatch(setUserMsg({ msg }))
            }
            onSuccess()
        } catch (err) {
            console.error('System mutation error:', err)
            const msg: UserMsg = { type: 'danger', txt: isEditMode ? 'Failed updating system' : 'Failed creating system' }
            dispatch(setUserMsg({ msg }))
        } finally {
            setIsSubmitting(false)
        }
    }

    const modalTitle = isEditMode ? 'Edit System' : 'Create System'

    return <div ref={trapRef}>
        <section className="system-form-modal" role="dialog" aria-modal="true" aria-label={modalTitle}>
            <header>
                <h2>{modalTitle}</h2>
                <button className="close-btn" onClick={onClose} aria-label="Close">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </header>

            <section className="modal-form-body">
                <div className="form-section-card">
                    <div className="form-section-label">System Details</div>
                    <div className="form-row">
                        <div className="field">
                            <FormInputText
                                keyName="name"
                                label="Name"
                                isRequired={!isEditMode}
                                isDisabled={isEditMode}
                                value={form.name}
                                onChange={onChangeText}
                            />
                        </div>
                        <div className="field">
                            <FormInputText
                                keyName="abbreviation"
                                label="Abbreviation"
                                isRequired={!isEditMode}
                                isDisabled={isEditMode}
                                value={form.abbreviation}
                                onChange={onChangeText}
                            />
                        </div>
                        <div className="field">
                            <div>
                                <label htmlFor="type">Type</label>
                                <RequiredAsterisk />
                            </div>
                            <input
                                type="text"
                                name="type"
                                id="type"
                                list="system-type-options"
                                placeholder="Select or type a new type"
                                value={form.type}
                                onChange={onChangeText}
                            />
                            <datalist id="system-type-options">
                                {existingTypes.map(t => <option key={t} value={t} />)}
                            </datalist>
                            <span></span>
                        </div>
                        <div className="field">
                            <FormInputText
                                keyName="logoUrl"
                                label="Logo URL"
                                isRequired={false}
                                value={form.logoUrl}
                                onChange={onChangeText}
                            />
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    {!isSubmitting && <button className="main" onClick={onSubmit} disabled={isSubmitting}>
                        {isEditMode ? 'Save' : 'Create'}
                    </button>}
                    {isSubmitting && <span className="spinner"><Spinner isSmall={true} /></span>}
                </div>
            </section>
        </section>
        <Backlog zIndex={199} onCloseBacklog={onClose} />
    </div>
}
